import React from 'react';
import PropTypes from 'prop-types';
import { keyframes } from '@emotion/react';
import { Box, Button, IconButton, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { RefreshCw, X } from 'lucide-react';

const DEFAULT_MESSAGE = 'Restart to load the latest SAGE. It takes a few seconds.';

// Yellow take on the app's banners (see Analysis/UnhealthyDevicesBanner).
const BANNER_BACKGROUND = '#fef3c7';
const BANNER_BORDER = '#fcd34d';
const ACCENT_YELLOW = '#b45309';
const TEXT_DARK = '#212322';

const slideIn = keyframes`
    from { opacity: 0; transform: translateY(24px); }
    to { opacity: 1; transform: translateY(0); }
`;

// Optional update: a card in the bottom-left corner; the page underneath stays usable.
const UpdateBanner = ({ message, onRestart, onDismiss }) => {
    const theme = useTheme();

    return (
        <Box
            role="status"
            sx={{
                position: 'fixed',
                left: { xs: 16, sm: 24 },
                right: { xs: 16, sm: 'auto' },
                bottom: { xs: 16, sm: 24 },
                width: { sm: 380 },
                px: 2.5,
                py: 2,
                zIndex: theme.zIndex.snackbar,
                borderRadius: 1,
                bgcolor: BANNER_BACKGROUND,
                border: `1px solid ${BANNER_BORDER}`,
                color: TEXT_DARK,
                boxShadow: '0 12px 32px rgba(0, 0, 0, 0.16)',
                animation: `${slideIn} 0.35s ease-out`
            }}
        >
            <Stack direction="row" spacing={1} alignItems="flex-start">
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack
                        direction="row"
                        spacing={0.75}
                        alignItems="center"
                        sx={{ color: ACCENT_YELLOW }}
                    >
                        <RefreshCw size={14} strokeWidth={2} />
                        <Typography
                            sx={{
                                fontSize: '11px',
                                fontWeight: 600,
                                letterSpacing: '0.12em',
                                textTransform: 'uppercase'
                            }}
                        >
                            Update available
                        </Typography>
                    </Stack>

                    <Typography
                        component="h2"
                        sx={{ mt: 0.75, fontSize: '18px', fontWeight: 600, lineHeight: 1.3 }}
                    >
                        New version of SAGE
                    </Typography>

                    <Typography sx={{ mt: 0.5, fontSize: '14px', color: '#595959' }}>
                        {message || DEFAULT_MESSAGE}
                    </Typography>

                    <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                        {/* Styled after the app's `components/common/Banner` button. */}
                        <Button
                            startIcon={<RefreshCw size={14} strokeWidth={2} />}
                            onClick={onRestart}
                            sx={{
                                bgcolor: 'common.white',
                                color: TEXT_DARK,
                                border: '1px solid #d9d9d9',
                                borderRadius: 1,
                                px: 1.5,
                                py: 0.625,
                                fontSize: '13px',
                                fontWeight: 500,
                                lineHeight: '22px',
                                textTransform: 'none',
                                boxShadow: '0px 2px 0px 0px rgba(0,0,0,0.02)',
                                '&:hover': {
                                    bgcolor: '#fafafa',
                                    border: '1px solid #d9d9d9'
                                }
                            }}
                        >
                            Restart now
                        </Button>
                        <Button
                            onClick={onDismiss}
                            sx={{
                                color: '#595959',
                                fontSize: '13px',
                                fontWeight: 500,
                                textTransform: 'none',
                                '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.05)' }
                            }}
                        >
                            Later
                        </Button>
                    </Stack>
                </Box>
                <IconButton
                    size="small"
                    aria-label="Close update banner"
                    onClick={onDismiss}
                    sx={{ mt: -0.5, mr: -1, color: '#595959' }}
                >
                    <X size={18} strokeWidth={1.5} />
                </IconButton>
            </Stack>
        </Box>
    );
};

UpdateBanner.propTypes = {
    message: PropTypes.string,
    onRestart: PropTypes.func.isRequired,
    onDismiss: PropTypes.func.isRequired
};

export default UpdateBanner;
